import AddIcon from '@mui/icons-material/Add';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadIcon from '@mui/icons-material/Download';
import LanguageIcon from '@mui/icons-material/Language';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import idmlServiceInstance from '../../services/IdmlService';
import { flatLangOptions } from '../subtitles/SelectLanguageComponent';
import IdmlReviewReportDialog from './IdmlReviewReportDialog';

const STAGE_COLOR = {
    NOT_STARTED: 'default',
    TRANSLATING: 'info',
    REVIEW: 'success',
    EXPORTING: 'info',
    EXPORTED: 'success',
    TRANSLATION_FAILED: 'error',
    EXPORT_FAILED: 'error',
};

// Plain-language stage names, since "processStage" values are internal jargon.
const STAGE_LABEL = {
    NOT_STARTED: 'Not started',
    TRANSLATING: 'Translating...',
    REVIEW: 'Ready to review',
    EXPORTING: 'Preparing download...',
    EXPORTED: 'Done',
    TRANSLATION_FAILED: 'Translation failed',
    EXPORT_FAILED: 'Download failed',
};

const IN_FLIGHT_STAGES = new Set(['TRANSLATING', 'EXPORTING']);

/**
 * Add/select target languages for a book; shows per-language processStage (design doc §7).
 *
 * The toolbar only shows a compact "current language" switcher -- with up to ~60 target
 * languages (design doc §"Key constraints") a chip-per-language strip doesn't scale, so the
 * full list + add/retry/retranslate actions live in a "Manage languages" popover instead.
 *
 * Export (design doc §5/§6, Milestone 4): once a language reaches REVIEW (or has previously
 * reached EXPORTED/EXPORT_FAILED), an "Export to .idml" action kicks off IdmlExportService async;
 * the resulting EXPORTING/EXPORTED/EXPORT_FAILED chip is already covered by STAGE_LABEL/
 * STAGE_COLOR above, and IdmlBookEditor.js already polls while any language is EXPORTING. Once
 * `exportedBlobUrl` is present, a "Download" action fetches and saves the exported .idml.
 */
const IdmlLanguageManager = ({ book, selectedLanguage, onSelectLanguage, onLanguageAdded, readOnly = false }) => {
    const [pendingLanguage, setPendingLanguage] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [startingLanguage, setStartingLanguage] = useState(null);
    const [exportingLanguage, setExportingLanguage] = useState(null);
    const [downloadingLanguage, setDownloadingLanguage] = useState(null);
    const [approvingLanguage, setApprovingLanguage] = useState(null);
    const [error, setError] = useState(null);
    const [manageAnchorEl, setManageAnchorEl] = useState(null);
    const [fontCatalog, setFontCatalog] = useState([]);
    const [savingFontOverrideFor, setSavingFontOverrideFor] = useState(null);
    const [reviewReportDialog, setReviewReportDialog] = useState(null);

    useEffect(() => {
        idmlServiceInstance
            .getFontCatalog()
            .then(setFontCatalog)
            .catch((err) => console.error('Failed to load font catalog:', err));
    }, []);

    const existingLanguages = useMemo(() => book?.targetLanguages || [], [book]);
    const sortedExistingLanguages = useMemo(
        () => [...existingLanguages].sort((a, b) => a.localeCompare(b)),
        [existingLanguages],
    );

    const failedCount = useMemo(
        () => existingLanguages.filter((code) => book?.targetLanguageStatus?.[code]?.processStage === 'TRANSLATION_FAILED').length,
        [existingLanguages, book],
    );

    const availableToAdd = useMemo(
        () =>
            flatLangOptions
                .map((l) => l.label)
                .filter((label) => label !== book?.sourceLanguage && !existingLanguages.includes(label))
                .sort((a, b) => a.localeCompare(b)),
        [book, existingLanguages],
    );

    const handleAddLanguage = async () => {
        if (!pendingLanguage) return;
        const addedLanguage = pendingLanguage;
        setIsAdding(true);
        setError(null);
        try {
            const updatedBook = await idmlServiceInstance.addLanguage(book.id, addedLanguage);
            setPendingLanguage('');
            if (onLanguageAdded) {
                onLanguageAdded(updatedBook);
            }
            // Switch straight to the language that was just added (it's now TRANSLATING) and close
            // the popover -- otherwise it's unclear anything happened, since the popover previously
            // stayed open showing the same "Add a language..." dropdown with no visible progress.
            if (onSelectLanguage) {
                onSelectLanguage(addedLanguage);
            }
            setManageAnchorEl(null);
        } catch (err) {
            console.error('Failed to add language:', err);
            setError(err.response?.data?.error || 'Failed to add language');
        } finally {
            setIsAdding(false);
        }
    };

    const handleStartTranslation = async (languageCode, { confirmOverwrite = false } = {}) => {
        if (confirmOverwrite) {
            const result = await Swal.fire({
                title: `Retranslate ${languageCode}?`,
                text: 'This regenerates the machine translation for every segment and overwrites its current text, including any manual review edits.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, retranslate',
                cancelButtonText: 'Cancel',
            });
            if (!result.isConfirmed) return;
        }
        setStartingLanguage(languageCode);
        setError(null);
        try {
            const updatedBook = await idmlServiceInstance.addLanguage(book.id, languageCode);
            if (onLanguageAdded) {
                onLanguageAdded(updatedBook);
            }
        } catch (err) {
            console.error('Failed to start translation:', err);
            setError(err.response?.data?.error || 'Failed to start translation');
        } finally {
            setStartingLanguage(null);
        }
    };

    const runExport = async (languageCode) => {
        setExportingLanguage(languageCode);
        setError(null);
        try {
            await idmlServiceInstance.exportBook(book.id, languageCode);
            // Optimistically flip the local stage to EXPORTING so the status chip (and
            // IdmlBookEditor's "poll while any language is EXPORTING" loop) pick this up
            // immediately, without waiting for the next poll tick.
            if (onLanguageAdded) {
                onLanguageAdded({
                    ...book,
                    targetLanguageStatus: {
                        ...book.targetLanguageStatus,
                        [languageCode]: {
                            ...book.targetLanguageStatus?.[languageCode],
                            processStage: 'EXPORTING',
                            errorMessage: null,
                        },
                    },
                });
            }
        } catch (err) {
            console.error('Failed to start export:', err);
            setError(err.response?.data?.error || 'Failed to start export');
        } finally {
            setExportingLanguage(null);
        }
    };

    const handleExport = async (languageCode) => {
        setExportingLanguage(languageCode);
        setError(null);
        // Since copy-fit never rewrites the IDML's own typography/geometry to force a fit (design
        // doc §4), a translation estimated not to fit, or simply never reviewed by a person,
        // is only ever flagged, never silently reworked, so surface that report here, before the
        // export is actually kicked off, rather than letting a user hand off a book with
        // unresolved flags without ever seeing them.
        let report = null;
        try {
            report = await idmlServiceInstance.getReviewReport(book.id, languageCode);
        } catch (reportErr) {
            console.error('Failed to load review report before export:', reportErr);
        }
        if (report && report.totalFlagged > 0) {
            setExportingLanguage(null);
            setReviewReportDialog({ report, languageCode });
            return;
        }
        await runExport(languageCode);
    };

    const handleApproveAllReviews = async (languageCode) => {
        const result = await Swal.fire({
            title: `Approve all flagged segments in ${languageCode}?`,
            text: 'Only segments currently marked "Needs review" in this language will be cleared. Segments that were already reviewed are left as-is, and no translated text is changed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, approve all',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;
        setApprovingLanguage(languageCode);
        setError(null);
        try {
            const { approvedCount } = await idmlServiceInstance.approveAllReviews(book.id, languageCode);
            Swal.fire({
                icon: 'success',
                title: approvedCount > 0 ? `Approved ${approvedCount} segment(s)` : 'Nothing needed approval',
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (err) {
            console.error('Failed to approve all reviews:', err);
            setError(err.response?.data?.error || 'Failed to approve all reviews');
        } finally {
            setApprovingLanguage(null);
        }
    };

    const handleSetFontOverride = async (languageCode, family) => {
        setSavingFontOverrideFor(languageCode);
        setError(null);
        try {
            const updatedStatus = await idmlServiceInstance.setFontOverride(book.id, languageCode, family || null);
            if (onLanguageAdded) {
                onLanguageAdded({
                    ...book,
                    targetLanguageStatus: {
                        ...book.targetLanguageStatus,
                        [languageCode]: updatedStatus,
                    },
                });
            }
        } catch (err) {
            console.error('Failed to set font override:', err);
            setError(err.response?.data?.error || 'Failed to set font override');
        } finally {
            setSavingFontOverrideFor(null);
        }
    };

    const handleDownload = async (languageCode) => {
        const blobUrl = book?.targetLanguageStatus?.[languageCode]?.exportedBlobUrl;
        if (!blobUrl) return;
        setDownloadingLanguage(languageCode);
        setError(null);
        try {
            const response = await fetch(blobUrl);
            if (!response.ok) throw new Error('Failed to download exported file');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `${book?.name || 'book'}-${languageCode}.idml`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
            }, 100);
        } catch (err) {
            console.error('Failed to download exported file:', err);
            setError('Failed to download the exported file');
        } finally {
            setDownloadingLanguage(null);
        }
    };

    // One icon covers Start / Retry / Retranslate -- only the tooltip and confirmation differ.
    const renderLanguageAction = (languageCode, stage) => {
        const exportedBlobUrl = book?.targetLanguageStatus?.[languageCode]?.exportedBlobUrl;

        // Download is offered whenever a previous export succeeded, even while re-translating
        // or re-exporting, since the earlier exported file is still valid until replaced.
        const downloadAction = exportedBlobUrl && (
            <Tooltip title="Download the exported .idml file">
                <span>
                    <IconButton
                        size="small"
                        onClick={() => handleDownload(languageCode)}
                        disabled={downloadingLanguage === languageCode}
                    >
                        {downloadingLanguage === languageCode ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
        );

        if (readOnly || IN_FLIGHT_STAGES.has(stage)) {
            return downloadAction || null;
        }

        if (startingLanguage === languageCode || exportingLanguage === languageCode || approvingLanguage === languageCode) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                    <CircularProgress size={16} />
                </Box>
            );
        }

        if (stage === 'NOT_STARTED') {
            return (
                <Tooltip title="Start translating this language">
                    <IconButton size="small" onClick={() => handleStartTranslation(languageCode)}>
                        <PlayArrowIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            );
        }

        if (stage === 'TRANSLATION_FAILED') {
            const reason = book?.targetLanguageStatus?.[languageCode]?.errorMessage;
            return (
                <Tooltip title={reason ? `Retry: ${reason}` : 'Retry the failed translation'}>
                    <IconButton size="small" color="error" onClick={() => handleStartTranslation(languageCode)}>
                        <ReplayIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            );
        }

        if (stage === 'REVIEW' || stage === 'EXPORTED' || stage === 'EXPORT_FAILED') {
            const exportReason = stage === 'EXPORT_FAILED' ? book?.targetLanguageStatus?.[languageCode]?.errorMessage : null;
            return (
                <Stack direction="row" alignItems="center" gap={0.25}>
                    <Tooltip title="Regenerate the machine translation for every segment in this language">
                        <IconButton size="small" onClick={() => handleStartTranslation(languageCode, { confirmOverwrite: true })}>
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Approve all flagged reviews for this language at once">
                        <IconButton size="small" onClick={() => handleApproveAllReviews(languageCode)}>
                            <DoneAllIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            stage === 'EXPORT_FAILED'
                                ? exportReason
                                    ? `Retry export: ${exportReason}`
                                    : 'Retry the failed export'
                                : 'Export to .idml'
                        }
                    >
                        <IconButton size="small" color={stage === 'EXPORT_FAILED' ? 'error' : 'default'} onClick={() => handleExport(languageCode)}>
                            <SaveAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {downloadAction}
                </Stack>
            );
        }

        return downloadAction || null;
    };

    return (
        <Stack direction="row" alignItems="center" gap={0.75}>
            {existingLanguages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No languages added yet —
                </Typography>
            ) : (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <Select
                        value={selectedLanguage || ''}
                        onChange={(e) => onSelectLanguage && onSelectLanguage(e.target.value)}
                        displayEmpty
                    >
                        {sortedExistingLanguages.map((languageCode) => {
                            const stage = book?.targetLanguageStatus?.[languageCode]?.processStage || 'NOT_STARTED';
                            return (
                                <MenuItem key={languageCode} value={languageCode}>
                                    {languageCode} · {STAGE_LABEL[stage] || stage}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            )}

            <Tooltip title="Manage languages">
                <IconButton size="small" onClick={(e) => setManageAnchorEl(e.currentTarget)}>
                    <Badge badgeContent={failedCount} color="error">
                        <LanguageIcon fontSize="small" />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(manageAnchorEl)}
                anchorEl={manageAnchorEl}
                onClose={() => setManageAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 380, maxHeight: 420, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                        Languages
                    </Typography>
                    <List dense sx={{ overflowY: 'auto', py: 0 }}>
                        {sortedExistingLanguages.map((languageCode) => {
                            const status = book?.targetLanguageStatus?.[languageCode];
                            const stage = status?.processStage || 'NOT_STARTED';
                            const stageLabel = STAGE_LABEL[stage] || stage;
                            return (
                                <ListItem key={languageCode} disablePadding secondaryAction={renderLanguageAction(languageCode, stage)}>
                                    <ListItemButton
                                        selected={selectedLanguage === languageCode}
                                        onClick={() => {
                                            onSelectLanguage && onSelectLanguage(languageCode);
                                            setManageAnchorEl(null);
                                        }}
                                    >
                                        <ListItemText
                                            primary={languageCode}
                                            secondary={
                                                <Stack component="span" direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        component="span"
                                                        size="small"
                                                        label={stageLabel}
                                                        color={STAGE_COLOR[stage] || 'default'}
                                                        variant="outlined"
                                                    />
                                                    {!readOnly && (fontCatalog.length > 0 || book?.uploadedFonts?.length > 0) && (
                                                        <FormControl
                                                            size="small"
                                                            variant="standard"
                                                            sx={{ minWidth: 90 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Select
                                                                displayEmpty
                                                                value={status?.fontOverride || ''}
                                                                onChange={(e) => handleSetFontOverride(languageCode, e.target.value)}
                                                                disabled={savingFontOverrideFor === languageCode}
                                                                sx={{ fontSize: 11 }}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Auto font</em>
                                                                </MenuItem>
                                                                {(book?.uploadedFonts || []).map((f) => (
                                                                    <MenuItem key={f.family} value={f.family} sx={{ fontSize: 12 }}>
                                                                        {f.family} (uploaded)
                                                                    </MenuItem>
                                                                ))}
                                                                {fontCatalog.map((entry) => (
                                                                    <MenuItem key={entry.family} value={entry.family} sx={{ fontSize: 12 }}>
                                                                        {entry.family}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>

                    {!readOnly && (
                        <>
                            <Divider />
                            <Stack direction="row" gap={1} sx={{ p: 1.5 }}>
                                <Autocomplete
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                    options={availableToAdd}
                                    value={pendingLanguage || null}
                                    onChange={(e, newValue) => setPendingLanguage(newValue || '')}
                                    disabled={isAdding || availableToAdd.length === 0}
                                    renderInput={(params) => <TextField {...params} placeholder="Add a language..." />}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={isAdding ? null : <AddIcon fontSize="small" />}
                                    onClick={handleAddLanguage}
                                    disabled={!pendingLanguage || isAdding}
                                >
                                    {isAdding ? <CircularProgress size={20} /> : 'Add'}
                                </Button>
                            </Stack>
                        </>
                    )}

                    {error && (
                        <Typography variant="caption" color="error" sx={{ px: 1.5, pb: 1.5 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </Popover>

            <IdmlReviewReportDialog
                open={Boolean(reviewReportDialog)}
                report={reviewReportDialog?.report}
                languageCode={reviewReportDialog?.languageCode}
                bookName={book?.name}
                onClose={() => setReviewReportDialog(null)}
                onExportAnyway={() => {
                    const languageCode = reviewReportDialog?.languageCode;
                    setReviewReportDialog(null);
                    if (languageCode) runExport(languageCode);
                }}
            />
        </Stack>
    );
};

export default IdmlLanguageManager;
